import { ErrorPayload } from './types';
import { IUser, UpdateUserPayload } from '@/@types/globals';
import api from '@/api';
import { API_ENDPOINTS } from '@/constants';

/**
 *
 * Checks if the user is logged in or not. If the user is not
 * logged in, this will also sent an OTP to the user's phone number
 *
 * @param contact The user's phone number
 * @returns Boolean denoting if the user is logged in or not
 */

export const fetchCustomerDetails = async (
  headers?: Record<string, string>,
  query: Record<string, string> = {},
): Promise<{
  success: boolean;
  data?: IUser;
  error?: string;
}> => {
  const response = await api(
    API_ENDPOINTS.CUSTOMERS,
    { ...query },
    {
      headers,
    },
  );

  const data = await response.json();
  if (response.ok) {
    const xHash = response.headers.get('Grpc-Metadata-X-Data-Hash');
    return {
      success: true,
      data: {
        ...data,
        headers: {
          xHash,
        },
      },
    };
  }
  return {
    success: false,
    error: (data as ErrorPayload).message || 'Error fetching customer',
  };
};

export const updateCustomerDetails = async (
  customerDetails: IUser,
  headers: Record<string, string>,
): Promise<UpdateUserPayload> => {
  if (!customerDetails.id) throw new Error('Invalid user id');

  try {
    const response = await api(
      `${API_ENDPOINTS.CUSTOMERS}/${customerDetails.id}`,
      {},
      { method: 'POST', headers, body: JSON.stringify(customerDetails) },
    );

    const data = await response.json();
    if (response.ok) {
      return {
        success: true,
        data: data as IUser,
      };
    } else {
      return {
        success: false,
        error: (data as ErrorPayload).message || 'Error updating PAN',
      };
    }
  } catch {
    return { success: false, error: 'Error updating PAN' };
  }
};

export const fetchNameFromPan = async (
  pan: string,
  headers: Record<string, string>,
): Promise<{
  error?: {
    code: string;
    description?: string;
  };
  data?: IUser;
  headers?: Record<string, string>;
}> => {
  const response = await api(
    API_ENDPOINTS.CUSTOMERS,
    { pan },
    {
      method: 'GET',
      headers,
    },
  );

  const data = (await response.json()) as IUser;
  if (response.ok) {
    const xHash = response.headers.get('Grpc-Metadata-X-Data-Hash') ?? '';
    return {
      data,
      headers: {
        xHash,
      },
    };
  } else {
    return {
      error: {
        description: (data as ErrorPayload).message || 'Error fetchin PAN',
        code: (data as ErrorPayload).details?.[0].code ?? 'BAD_REQUEST_ERROR',
      },
    };
  }
};
